import { Fragment, useEffect, useState } from 'react';
import plusCircleIcon from '../../../../assets/img/button-icons/plus-circle-purple.svg';
import './style.css';
import {
  useGetAllCatalogMeds,
  useUpdateCatalogOrderSet,
  type UpdateCatalogOrderSetFormValue,
} from '../MedicationOrderSets/hooks';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  mapToMedicationDetails,
  mapToOrderSetMedicationDetails,
} from './helper';
import editPenSolidIcon from '../../../../assets/img/button-icons/edit-pen-solid.svg';
import trashRedSolidIcon from '../../../../assets/img/button-icons/trash-red.svg';
import { MedFormInOrderSet } from '../MedForm';
import { CatalogOrderSet } from '../MedicationOrderSets/types';
import AsyncSelect from 'react-select/async';
import { tranformTaperFormat } from '../../../../lib/transferObjects/Medication';

export const OrderSetForm = ({
  orderSetData,
  onCancel,
  onCancelAndRerender,
  setToast,
}: {
  orderSetData: CatalogOrderSet;
  onCancel: () => void;
  onCancelAndRerender: () => void;
  setToast: (message: string) => void;
}) => {
  const [addingMedication, setAddingMedication] = useState(false);
  const { postUpdateCatalogOrderSet, errorMessage, isLoading } =
    useUpdateCatalogOrderSet();
  const [activeMediEditId, setActiveMediEditId] = useState('');
  const { register, getValues, control, watch } = useForm({
    defaultValues: {
      orderSetId: orderSetData?.id || null,
      orderSetName: orderSetData?.orderSetName || '',
      isActive: !!orderSetData?.isActive,
      isDeleted: false,
      note: orderSetData?.note || '',
      medications: Array.isArray(orderSetData?.medications)
        ? orderSetData.medications.map((item) =>
            mapToOrderSetMedicationDetails(item),
          )
        : [],
    },
  });
  const selectedMedForm = useForm({
    defaultValues: {
      selectedMed: null,
    },
  });
  const { fields, append, update, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: 'medications', // unique name for your Field Array
  });
  const currentMedications = watch('medications');
  const { postGetAllCatalogMeds } = useGetAllCatalogMeds();

  const handleCreateOrderSet = async () => {
    setAddingMedication(false);
    const formValues = getValues();
    const payload: UpdateCatalogOrderSetFormValue = {
      ...formValues,
      medications: (formValues.medications || []).map((item) => {
        const {
          medId,
          medName,
          brand,
          subheading,
          furtherInformation,
          indication,
          indicationDescription,
          medSectionBreak,
          medChangeType,
          furtherDescriptionOfChange,
          isDeleted,
          regimen,
        } = item;
        return {
          medId,
          medName,
          brand,
          subheading,
          furtherInformation,
          indicationDescription,
          indication,
          medSectionBreak,
          medChangeType,
          furtherDescriptionOfChange,
          isDeleted,
          regimen: regimen
            ? {
                ...regimen,
                taper: tranformTaperFormat(regimen?.taper),
              }
            : undefined,
        };
      }),
    };
    await postUpdateCatalogOrderSet(payload);

    onCancelAndRerender && onCancelAndRerender();
  };

  const loadMedicineOptions = async (inputValue: string) => {
    if (inputValue && inputValue.length > 3) {
      const result = await postGetAllCatalogMeds({
        filterPayload: {
          medName: inputValue,
        },
      });
      return result?.map((item: any) => {
        return {
          label: item.med_name,
          value: mapToMedicationDetails(item),
        };
      });
    }
    return [];
  };

  useEffect(() => {
    if (errorMessage && setToast) {
      setToast(errorMessage);
    }
  }, [errorMessage]);

  return (
    <>
      <div className="create-order-set-form-container">
        <div className="medication-row order-set-form-header">
          <div style={{ display: 'flex' }}>
            <label
              style={{
                textWrap: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                cursor: 'default',
                marginRight: '10px',
                marginLeft: '5px',
              }}
            >
              Name of Order set
            </label>
            <input
              style={{
                width: '30vw',
              }}
              className="manage-task-input form-control"
              type="text"
              placeholder="Enter a name for this order set"
              {...register('orderSetName')}
            />
          </div>
          <div>
            {!addingMedication ? (
              <button
                className="btn btnSecondary medication-button"
                type="button"
                onClick={() => setAddingMedication(true)}
              >
                Add medication{' '}
                <img src={plusCircleIcon} alt="add medication icon" />
              </button>
            ) : null}
          </div>
        </div>
        {addingMedication ? (
          <div className="create-order-set-form">
            <div className="heading">Add medication</div>
            <div className="med-form-container-row">
              <div className="filterItem">
                <label className="txtDropdownLabel">
                  Medication name and strength
                </label>
                <div className="view-width-30">
                  <AsyncSelect
                    isMulti={false}
                    placeholder={'Start typing and select'}
                    cacheOptions
                    defaultOptions
                    isClearable
                    loadOptions={(inputValue) =>
                      loadMedicineOptions(inputValue)
                    }
                    onChange={(selectedOptions) => {
                      selectedMedForm.setValue(
                        'selectedMed',
                        // @ts-expect-error
                        selectedOptions.value,
                      );
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="mt-4">
              <button
                className="btn"
                type="button"
                style={{ width: '150px', fontWeight: 'bold' }}
                onClick={() => {
                  setAddingMedication(false);
                  selectedMedForm.setValue('selectedMed', null);
                }}
              >
                Cancel & close
              </button>
              <button
                className="btn btnPrimary"
                type="button"
                style={{ width: '150px' }}
                onClick={() => {
                  setAddingMedication(false);
                  const selectedOptions =
                    selectedMedForm.getValues('selectedMed');
                  if (selectedOptions) append(selectedOptions);
                  selectedMedForm.setValue('selectedMed', null);
                }}
              >
                Save
              </button>
            </div>
          </div>
        ) : null}
        {fields.map((medication, index) => {
          return (
            <Fragment key={medication.id}>
              {!medication?.isDeleted ? (
                <>
                  <div className="text-bold set-medication-row">
                    <div>{medication.medName}</div>
                    <div>
                      <button
                        onClick={() => {
                          // for existing order set, we market isDeleted to true
                          if (orderSetData?.id) {
                            const deletedMed = {
                              ...medication,
                              isDeleted: true,
                            };
                            update(index, deletedMed);
                          } else {
                            remove(index);
                          }
                        }}
                      >
                        <img
                          src={trashRedSolidIcon}
                          className="icon"
                          alt="edit pen icon"
                        />
                      </button>
                      <button
                        style={{ marginLeft: '30px' }}
                        onClick={() => setActiveMediEditId(medication.id)}
                      >
                        <img
                          src={editPenSolidIcon}
                          className="icon"
                          alt="edit pen icon"
                        />
                      </button>
                    </div>
                  </div>
                  {activeMediEditId === medication.id ? (
                    <MedFormInOrderSet
                      data={medication}
                      onCancelEdit={() => setActiveMediEditId('')}
                      onOrderSetModeChange={(values: any) => {
                        const indexToUpdate = index;
                        update(indexToUpdate, values);
                      }}
                    />
                  ) : null}
                </>
              ) : null}
            </Fragment>
          );
        })}
        <div className="create-order-set-actions">
          <button
            className="btn"
            type="button"
            style={{ width: '150px', fontWeight: 'bold' }}
            onClick={() => {
              setAddingMedication(false);
              onCancel();
            }}
          >
            Cancel & close
          </button>
          <button
            disabled={
              addingMedication || currentMedications.length === 0 || isLoading
            }
            className="btn btnPrimary"
            type="button"
            onClick={handleCreateOrderSet}
          >
            {orderSetData ? 'Save set ' : 'Create order set'}
            <i
              className="fa-solid fa-circle-check fa-md"
              style={{ marginLeft: '5px' }}
            />
          </button>
        </div>
      </div>
    </>
  );
};
